/**
 * The puja's Cities tab (S11, R215): where this puja is bookable city by city, as exceptions to
 * its one national switch (03-data/08 §3.14, 00-product/10 §6).
 *
 * **Only the exceptions are listed.** A city with no row follows the national switch, and the
 * server resolves that; the tab never recomputes whether a city is bookable, so it cannot
 * disagree with what a devotee is shown.
 *
 * **Every change carries a reason**, audited with the whole row. The note is ops' own and is
 * never rendered to a devotee.
 *
 * No design shows this; built from the docs above per the 2026-10-26 ruling.
 */
import { useState } from 'react';
import { Input, Select } from '../components/forms';
import { DataTable } from '../components/data';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { ConfirmAction, ScopeAction, Section } from '../components/kit';
import { StatusChip } from '../components/primitives';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { formatDateTime } from '../lib/datetime';
import type { Scope } from '../lib/rbac';
import { useCities } from '../lib/api/hooks/geo';
import {
  useCityAvailability,
  useClearCityAvailability,
  useSetCityAvailability,
  type CityAvailabilityRow,
} from '../lib/api/hooks/catalogue';

const OPEN = 'Bookable here';
const CLOSED = 'Not bookable here';

export function PujaCityAvailability({ scope, pujaId }: { scope: Scope; pujaId: number }) {
  const { toast } = useConsole();
  const exceptions = useCityAvailability(pujaId);
  const cities = useCities({ page: 1, page_size: 200 });
  const set = useSetCityAvailability();
  const clear = useClearCityAvailability();
  const [cityName, setCityName] = useState('');
  const [state, setState] = useState(CLOSED);
  const [note, setNote] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [dropping, setDropping] = useState<CityAvailabilityRow | null>(null);

  const cityRows = cities.data?.items ?? [];
  const nameOf = (id: number) => cityRows.find((c) => c.id === id)?.name ?? `city #${id}`;
  const cityId = cityRows.find((c) => c.name === cityName)?.id;
  const fail = (e: unknown) => toast({ tone: 'error', message: errorMessage(e) });

  return (
    <Section title="Cities" note="exceptions to the national switch · the server resolves the rest">
      {exceptions.isPending && <LoadingState message="Reading the city exceptions…" />}
      {exceptions.isError && (
        <ErrorState title="Could not read the city exceptions" cause={errorMessage(exceptions.error)} />
      )}
      {exceptions.isSuccess && exceptions.data.items.length === 0 && (
        <EmptyState inline title="No city exception" body="Every city follows this puja's national switch." />
      )}
      {exceptions.isSuccess && exceptions.data.items.length > 0 && (
        <DataTable
          density="compact"
          columns={[
            { key: 'city', label: 'City' },
            { key: 'state', label: 'Here' },
            { key: 'note', label: 'Ops note' },
            { key: 'updated', label: 'Changed', mono: true },
            { key: 'act', label: '' },
          ]}
          rows={exceptions.data.items.map((row) => ({
            id: row.city_id,
            city: nameOf(row.city_id),
            state: row.is_bookable ? (
              <StatusChip tone="success" label={OPEN} />
            ) : (
              <StatusChip tone="warn" label={CLOSED} />
            ),
            note: row.note ?? '—',
            updated: formatDateTime(row.updated_at),
            act: (
              <ScopeAction scope={scope} need="catalogue_content" size="sm" variant="secondary" onClick={() => setDropping(row)}>
                Follow the national switch
              </ScopeAction>
            ),
          }))}
        />
      )}

      <div style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'flex-end', marginTop: 'var(--sp-md)', flexWrap: 'wrap' }}>
        <Select
          label="City"
          value={cityName}
          options={cityRows.map((c) => c.name)}
          onChange={setCityName}
          placeholder="Pick a city"
        />
        <Select label="Here" value={state} options={[CLOSED, OPEN]} onChange={setState} />
        <Input label="Ops note (never shown to devotees)" value={note} onChange={setNote} density="admin" />
        <ScopeAction scope={scope} need="catalogue_content" size="sm" disabled={cityId === undefined} onClick={() => setConfirming(true)}>
          Set for this city
        </ScopeAction>
      </div>

      <ConfirmAction
        open={confirming}
        title={`${state === OPEN ? 'Open' : 'Close'} ${cityName} for this puja?`}
        phrase={state === OPEN ? 'OPEN' : 'CLOSE'}
        tone="warn"
        reason
        consequences={[
          state === OPEN
            ? `Devotees in ${cityName} can book this puja even while it is switched off nationally.`
            : `Devotees in ${cityName} can no longer book this puja; bookings already made are not touched.`,
          'Recorded in the audit log with your reason.',
        ]}
        onCancel={() => setConfirming(false)}
        onConfirm={(reason) =>
          set.mutate(
            {
              pujaId,
              cityId: cityId!,
              is_bookable: state === OPEN,
              ...(note.trim() === '' ? {} : { note: note.trim() }),
              reason,
            },
            {
              onSuccess: () => {
                toast({ tone: 'success', message: `${cityName} set for this puja` });
                setConfirming(false);
                setNote('');
              },
              onError: fail,
            },
          )
        }
        confirmLabel="Save"
      />

      <ConfirmAction
        open={dropping !== null}
        title={`Let ${dropping ? nameOf(dropping.city_id) : ''} follow the national switch?`}
        phrase="DROP"
        tone="warn"
        reason
        consequences={["The exception is removed, and this city is bookable exactly when the puja is switched on nationally."]}
        onCancel={() => setDropping(null)}
        onConfirm={(reason) =>
          clear.mutate(
            { pujaId, cityId: dropping!.city_id, reason },
            {
              onSuccess: () => {
                toast({ tone: 'success', message: 'Exception dropped' });
                setDropping(null);
              },
              onError: fail,
            },
          )
        }
        confirmLabel="Drop"
      />
    </Section>
  );
}
